import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Modal } from '@mui/material';
import { useNav, useUser } from '../App';
import { C } from '../theme';
import api from '../services/api';

export default function Waitlist() {
  const navigate = useNav();
  const { setUser } = useUser();

  const [code, setCode]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [venues, setVenues]     = useState([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [oracleOpen, setOracleOpen]   = useState(false);
  const [oracleText, setOracleText]   = useState('');

  async function handleOracle() {
    const today = new Date().toISOString().slice(0, 10);
    const cacheKey = 'oracle_cache_' + today;
    const cached = localStorage.getItem(cacheKey);
    if (cached) { setOracleText(cached); setOracleOpen(true); return; }
    try {
      const r = await api.get('/oracle');
      const oracles = r.data.oracles;
      if (!oracles || !oracles.length) return;
      let order = JSON.parse(localStorage.getItem('oracle_order') || 'null');
      let pos = parseInt(localStorage.getItem('oracle_pos') || '0', 10);
      if (!order || order.length !== oracles.length) {
        order = oracles.map((_, i) => i).sort(() => Math.random() - 0.5);
        pos = 0;
      }
      const text = oracles[order[pos]];
      const nextPos = (pos + 1) % oracles.length;
      if (nextPos === 0) {
        order = oracles.map((_, i) => i).sort(() => Math.random() - 0.5);
      }
      localStorage.setItem('oracle_order', JSON.stringify(order));
      localStorage.setItem('oracle_pos', String(nextPos));
      localStorage.setItem(cacheKey, text);
      setOracleText(text);
      setOracleOpen(true);
    } catch (_) {}
  }



  // Load venue list (read-only — no GPS required, just show all launch venues)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/venues');
        setVenues((r.data.venues || []).slice(0, 5));
      } catch (_) {
        // silently swallow — venues are a bonus, not critical
      } finally {
        setVenuesLoading(false);
      }
    })();
  }, []);

  const handleRedeem = useCallback(async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const r = await api.post('/referral/redeem', { code: trimmed });
      if (r.data.status === 'success') {
        setSuccess(r.data.message || 'Your account is now active!');
        // Update global user state so App.js re-routes without reload
        setUser(r.data.user);
        setTimeout(() => navigate('/home'), 800);
      } else {
        setError(r.data.message || 'Something went wrong.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not apply referral code.');
    }
    setLoading(false);
  }, [code, navigate, setUser]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join me on Binko',
        text: 'Meet people in the same bar, right now. No swiping.',
        url: 'https://app.binko.me',
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText('https://app.binko.me');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: C.bgApp,
      padding: '24px 16px 40px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxWidth: '480px',
      margin: '0 auto',
    }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '8px', pb: '4px' }}>
        <img src="/logo.png" alt="binko" style={{ height: '44px', width: 'auto' }} />
      </Box>

      {/* Hero message */}
      <Box sx={{
        background: C.bgCard,
        border: `0.5px solid rgba(255,255,255,0.07)`,
        borderRadius: '16px',
        padding: '20px 18px',
        textAlign: 'center',
      }}>
        <Typography sx={{ fontSize: '28px', mb: '6px' }}>🎉</Typography>
        <Typography sx={{ fontSize: '20px', fontWeight: 600, color: C.textPrimary, mb: '8px' }}>
          You're on the list
        </Typography>
        <Typography sx={{ fontSize: '14px', color: C.textMuted, lineHeight: 1.6 }}>
          Binko is rolling out carefully. Have a referral code? Enter it below to unlock access now.
        </Typography>
      </Box>

      {/* Referral code entry */}
      <Box sx={{
        background: C.bgCard,
        border: `0.5px solid rgba(255,255,255,0.07)`,
        borderRadius: '16px',
        padding: '18px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: C.textPrimary, letterSpacing: '0.3px' }}>
          Have a referral code?
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g. ABCD1234"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          inputProps={{ style: { textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 } }}
          disabled={loading || !!success}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: C.textPrimary,
              borderRadius: '10px',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
              '&:hover fieldset': { borderColor: `rgba(251,188,21,0.35)` },
              '&.Mui-focused fieldset': { borderColor: C.yellow },
            },
            '& input': { color: C.textPrimary, fontSize: '15px' },
          }}
        />

        {error && (
          <Typography sx={{ fontSize: '13px', color: C.orange }}>{error}</Typography>
        )}
        {success && (
          <Typography sx={{ fontSize: '13px', color: C.teal }}>{success}</Typography>
        )}

        <Button
          fullWidth
          onClick={handleRedeem}
          disabled={loading || !code.trim() || !!success}
          sx={{
            background: C.yellow,
            color: C.yellowText,
            borderRadius: '10px',
            padding: '11px',
            fontSize: '14px',
            fontWeight: 600,
            '&:hover': { background: '#e6ac10' },
            '&:disabled': { background: 'rgba(251,188,21,0.25)', color: 'rgba(0,0,0,0.35)' },
          }}
        >
          {loading
            ? <CircularProgress size={20} sx={{ color: C.yellowText }} />
            : 'Unlock Access'}
        </Button>
      </Box>

      {/* Love Oracle Button + Modal */}
      <Button
        fullWidth
        onClick={handleOracle}
        sx={{
          border: `0.5px solid rgba(251,188,21,0.3)`,
          color: C.yellow,
          borderRadius: '12px',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          '&:hover': { background: 'rgba(251,188,21,0.06)' },
        }}
      >
        Love Oracle ✨
      </Button>

      <Modal open={oracleOpen} onClose={() => setOracleOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '85%', maxWidth: 360,
          background: C.bgCard,
          border: `0.5px solid rgba(251,188,21,0.25)`,
          borderRadius: '16px', p: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: '28px', lineHeight: 1 }}>✨</Typography>
          <Typography sx={{ fontSize: '10px', color: C.yellow, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 500 }}>
            Today's Love Oracle
          </Typography>
          <Typography sx={{ fontSize: '15px', color: C.textPrimary, lineHeight: 1.65, fontStyle: 'italic' }}>
            "{oracleText}"
          </Typography>
          <Typography sx={{ fontSize: '11px', color: C.textDisabled }}>Changes at midnight</Typography>
          <Button onClick={() => setOracleOpen(false)} sx={{
            border: `0.5px solid rgba(251,188,21,0.3)`,
            color: C.yellow,
            borderRadius: '20px',
            width: '100%',
            fontSize: '13px',
          }}>Close</Button>
        </Box>
      </Modal>

      {/* Nearby venues — read only */}
      <Box>
        <Typography sx={{ fontSize: '11px', color: C.textDisabled, letterSpacing: '0.8px', textTransform: 'uppercase', mb: '8px' }}>
          Binko launch venues
        </Typography>
        {venuesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '12px' }}>
            <CircularProgress size={20} sx={{ color: C.textDisabled }} />
          </Box>
        ) : venues.length === 0 ? (
          <Typography sx={{ fontSize: '13px', color: C.textMuted }}>No venues found.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {venues.map(v => (
              <Box key={v.id} sx={{
                background: C.bgCard,
                border: `0.5px solid rgba(255,255,255,0.07)`,
                borderRadius: '12px',
                padding: '12px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <Box>
                  <Typography sx={{ fontSize: '14px', color: C.textPrimary, fontWeight: 500 }}>
                    {v.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: C.textMuted, mt: '2px' }}>
                    {v.area}
                  </Typography>
                </Box>
                <Typography sx={{
                  fontSize: '11px',
                  color: C.textDisabled,
                  background: 'rgba(255,255,255,0.04)',
                  border: `0.5px solid rgba(255,255,255,0.08)`,
                  borderRadius: '6px',
                  padding: '4px 8px',
                }}>
                  Coming soon
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Share CTA */}
      <Box sx={{
        background: C.bgCard,
        border: `0.5px solid rgba(255,255,255,0.07)`,
        borderRadius: '14px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        textAlign: 'center',
      }}>
        <Typography sx={{ fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
          Know someone who'd love Binko?{' '}
          <span style={{ color: C.textPrimary }}>Spread the word</span> — the more people check in, the better the nights.
        </Typography>
        <Button
          onClick={handleShare}
          sx={{
            border: `0.5px solid rgba(255,255,255,0.15)`,
            color: C.textPrimary,
            borderRadius: '10px',
            padding: '8px 20px',
            fontSize: '13px',
            '&:hover': { background: 'rgba(255,255,255,0.05)' },
          }}
        >
          Share Binko
        </Button>
      </Box>

      {/* Locked bottom nav — My Card only active */}
      <Box sx={{
        position: 'sticky', bottom: 0, zIndex: 100,
        height: '64px',
        background: '#1A1A1A',
        borderTop: '0.5px solid #2A2A2A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '0 8px',
        flexShrink: 0,
      }}>
        {[
          { label: 'In Here', active: false, icon: (c) => (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          )},
          { label: 'Play', active: false, icon: (c) => (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          )},
          { label: 'Home', active: false, icon: (c) => (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><path d="M9 21v-8h6v8"/>
            </svg>
          )},
          { label: 'Chats', active: false, icon: (c) => (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          )},
          { label: 'My Card', active: true, icon: (c) => (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          )},
        ].map(tab => (
          <Box
            key={tab.label}
            onClick={() => tab.active ? navigate('/profile') : null}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '3px', padding: '6px 12px', borderRadius: '12px',
              minWidth: '56px',
              cursor: tab.active ? 'pointer' : 'default',
              opacity: tab.active ? 1 : 0.3,
            }}
          >
            {tab.icon(tab.active ? '#FBBC15' : '#555555')}
            <Typography sx={{
              fontSize: '9px', fontWeight: 500, letterSpacing: '0.2px',
              color: tab.active ? '#FBBC15' : '#555555',
            }}>
              {tab.label}
            </Typography>
          </Box>
        ))}
      </Box>

    </Box>
  );
}
